/* ===========================================================================
   The 360 renderer: WebGL, ~120 lines, no library.
   ---------------------------------------------------------------------------
   There is no sphere mesh here. The panorama is drawn with ONE triangle
   covering the screen, and the fragment shader turns each pixel back into a
   look direction, then that direction into a coordinate in the equirectangular
   image. Two things follow:

     · quality is per-pixel, not per-vertex: no polygon edges at the poles, the
       place every low-poly sphere viewer shows its seams;
     · there is nothing to tessellate, so the whole viewer stays small enough to
       read in one sitting — which is why this template needs no 3D dependency
       at all (and why it can never break because a 3D library changed its API).
   =========================================================================== */

export interface PanoView {
  /** radians, 0 = looking at the centre of the image */
  yaw: number;
  /** radians, clamped by the caller to ±85° */
  pitch: number;
  /** vertical field of view, radians */
  fov: number;
}

export interface PanoRenderer {
  setImage: (image: HTMLImageElement) => void;
  draw: (view: PanoView) => void;
  resize: () => void;
  destroy: () => void;
}

const VERTEX = `
attribute vec2 aPos;
void main() {
  gl_Position = vec4(aPos, 0.0, 1.0);
}
`;

const FRAGMENT = `
precision highp float;
uniform sampler2D uImage;
uniform vec2 uResolution;
uniform float uYaw;
uniform float uPitch;
uniform float uFov;

const float PI = 3.141592653589793;

void main() {
  // Pixel → direction in camera space (the camera looks down -z).
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  float focal = 0.5 / tan(uFov * 0.5);
  vec3 dir = normalize(vec3(p.x, p.y, -focal));

  float cp = cos(uPitch), sp = sin(uPitch);
  dir = vec3(dir.x, dir.y * cp - dir.z * sp, dir.y * sp + dir.z * cp);

  float cy = cos(uYaw), sy = sin(uYaw);
  dir = vec3(dir.x * cy + dir.z * sy, dir.y, -dir.x * sy + dir.z * cy);

  // Direction → equirectangular texel.
  float u = atan(dir.x, -dir.z) / (2.0 * PI) + 0.5;
  float v = 0.5 - asin(clamp(dir.y, -1.0, 1.0)) / PI;
  gl_FragColor = texture2D(uImage, vec2(u, v));
}
`;

function compile(gl: WebGLRenderingContext, type: number, source: string): WebGLShader | null {
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    gl.deleteShader(shader);
    return null;
  }
  return shader;
}

/**
 * Copy any image into a power-of-two canvas.
 *
 * WebGL 1 refuses to repeat or mipmap a texture whose sides are not powers of
 * two, and repeating across the seam is exactly what a 360 image needs — the
 * left edge IS the right edge. Normalising here means an 8000×4000 photo from a
 * phone and a 1536×768 drawn panorama arrive at the GPU as the same kind of
 * object, and oversized uploads get scaled down to something the device can
 * actually hold.
 */
function toPowerOfTwo(image: HTMLImageElement, maxSize: number): TexImageSource {
  const width = image.naturalWidth || image.width;
  const height = image.naturalHeight || image.height;
  const isPot = (value: number) => (value & (value - 1)) === 0;
  if (isPot(width) && isPot(height) && width <= maxSize) return image;

  let target = 1;
  while (target * 2 <= Math.min(width, maxSize)) target *= 2;
  target = Math.max(1024, target);

  const canvas = document.createElement('canvas');
  canvas.width = target;
  canvas.height = target / 2;
  const context = canvas.getContext('2d');
  if (!context) return image;
  context.imageSmoothingQuality = 'high';
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  return canvas;
}

export function createRenderer(canvas: HTMLCanvasElement): PanoRenderer | null {
  const gl = (canvas.getContext('webgl', {alpha: false, antialias: false}) ??
    canvas.getContext('experimental-webgl', {alpha: false})) as WebGLRenderingContext | null;
  if (!gl) return null;

  const vertex = compile(gl, gl.VERTEX_SHADER, VERTEX);
  const fragment = compile(gl, gl.FRAGMENT_SHADER, FRAGMENT);
  const program = gl.createProgram();
  if (!vertex || !fragment || !program) return null;

  gl.attachShader(program, vertex);
  gl.attachShader(program, fragment);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return null;
  gl.useProgram(program);

  const buffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  // One oversized triangle instead of two: fewer vertices, and no diagonal
  // where the two halves of a quad meet.
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
  const position = gl.getAttribLocation(program, 'aPos');
  gl.enableVertexAttribArray(position);
  gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

  const uImage = gl.getUniformLocation(program, 'uImage');
  const uResolution = gl.getUniformLocation(program, 'uResolution');
  const uYaw = gl.getUniformLocation(program, 'uYaw');
  const uPitch = gl.getUniformLocation(program, 'uPitch');
  const uFov = gl.getUniformLocation(program, 'uFov');

  const texture = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.uniform1i(uImage, 0);

  const maxSize = gl.getParameter(gl.MAX_TEXTURE_SIZE) as number;
  let ready = false;

  const resize = () => {
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    const width = Math.max(1, Math.round(canvas.clientWidth * ratio));
    const height = Math.max(1, Math.round(canvas.clientHeight * ratio));
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }
    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.uniform2f(uResolution, canvas.width, canvas.height);
  };

  return {
    destroy() {
      gl.deleteTexture(texture);
      gl.deleteBuffer(buffer);
      gl.deleteProgram(program);
      gl.deleteShader(vertex);
      gl.deleteShader(fragment);
      // Free the drawing buffer immediately: a browser only keeps a handful of
      // live WebGL contexts, and a gallery can open many panoramas in a row.
      gl.getExtension('WEBGL_lose_context')?.loseContext();
    },

    draw(view) {
      if (!ready) return;
      resize();
      gl.uniform1f(uYaw, view.yaw);
      gl.uniform1f(uPitch, view.pitch);
      gl.uniform1f(uFov, view.fov);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    },

    resize,

    setImage(image) {
      gl.bindTexture(gl.TEXTURE_2D, texture);
      // No flip: the shader's v=0 is the zenith, which is the FIRST row of an
      // equirectangular image. Flipping here would hang the sky under the floor.
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 0);
      gl.texImage2D(
        gl.TEXTURE_2D,
        0,
        gl.RGB,
        gl.RGB,
        gl.UNSIGNED_BYTE,
        toPowerOfTwo(image, maxSize),
      );
      ready = true;
    },
  };
}
